import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card, CardHeader, CardBody, CardFooter, Button, Checkbox } from '@nextui-org/react';
import { Loader2, ChevronDown, ChevronUp } from 'lucide-react';

const ModelComparisonChart = ({ promptId, modelName='', availableModels = [], promptTests = [], promptVersions = [] }) => {
  const [selectedModels, setSelectedModels] = useState([]);
  const [selectedTestIds, setSelectedTestIds] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [colorMap, setColorMap] = useState({});
  const [showModelSelect, setShowModelSelect] = useState(true);
  const [showTestSelect, setShowTestSelect] = useState(true);
  const [normalizeScores, setNormalizeScores] = useState(false);
  const [showDifference, setShowDifference] = useState(false);
  
  // Line styles for different models
  const lineStyles = [
    { strokeDasharray: "0" },           // Solid line (default)
    { strokeDasharray: "5 5" },         // Dashed line
    { strokeDasharray: "2 2" },         // Dotted line
    { strokeDasharray: "10 5 2 5" },    // Dash-dot line
    { strokeDasharray: "10 5 2 5 2 5" } // Dash-dot-dot line
  ];
  
  // Generate colors for tests
  useEffect(() => {
    if (promptTests && promptTests.length > 0) {
      const colors = {};
      const baseColors = [
        '#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#a4de6c', 
        '#d0ed57', '#83a6ed', '#8dd1e1', '#6b486b', '#ff7300',
        '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#9F88FF'
      ];
      
      promptTests.forEach((test, index) => {
        colors[test.test_id] = baseColors[index % baseColors.length];
      });
      
      setColorMap(colors);
    };
    if (promptTests && promptTests.length > 0 && selectedTestIds.length === 0) {
      handleToggleAllTests();
    }
  }, [promptTests]);
  
  // Set initial models when available
  useEffect(() => {
    if (availableModels && availableModels.length > 0 && selectedModels.length === 0) {
      // Default to selecting first two models if available
      setSelectedModels(modelName? [modelName]: availableModels.slice(0, Math.min(1, availableModels.length)));
    }
  }, [availableModels]);


  // Process data for chart whenever selections change
  useEffect(() => {
    if (promptVersions.length === 0 || selectedTestIds.length === 0 || selectedModels.length === 0) {
      setChartData([]);
      return;
    }

    prepareChartData();
  }, [selectedModels, selectedTestIds, promptTests, promptVersions, normalizeScores, showDifference]);

  // Prepare data for the chart
  const prepareChartData = () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Get all versions to display on the x-axis
      const versionPoints = promptVersions.map(v => v.version);
      
      // Create data points for each version
      const formattedData = versionPoints.map(version => {
        const point = { version };
        
        // Calculate baseline scores if showing differences
        const baselineScores = {};
        if (showDifference && selectedModels.length > 1) {
          const baselineModel = selectedModels[0];
          selectedTestIds.forEach(testId => {
            const test = promptTests.find(t => t.test_id === testId);
            if (test) {
              const metricsKey = `${baselineModel}_average_metrics`;
              if (test[metricsKey] && test[metricsKey][version]) {
                baselineScores[testId] = parseFloat(test[metricsKey][version].avg_score || 0);
              }
            }
          });
        }
        
        // For each selected model and test combination
        selectedModels.forEach(model => {
          selectedTestIds.forEach(testId => {
            const test = promptTests.find(t => t.test_id === testId);
            if (test) {
              const metricsKey = `${model}_average_metrics`;
              const modelTestKey = `${model}:${test.name}`;
              
              if (test[metricsKey] && test[metricsKey][version]) {
                let score = parseFloat(test[metricsKey][version].avg_score || 0);
                
                // If showing differences and not the baseline model
                if (showDifference && selectedModels.length > 1 && model !== selectedModels[0]) {
                  if (baselineScores[testId] !== undefined) {
                    score = score - baselineScores[testId]; // Show difference from baseline
                  }
                }
                
                // Normalize scores if enabled (percentage of maximum possible score)
                if (normalizeScores) {
                  const threshold = parseFloat(test.threshold || 100);
                  score = (score / threshold) * 100;
                }
                
                point[modelTestKey] = score;
              } else {
                point[modelTestKey] = null; // No data for this version
              }
            }
          });
        });
        
        // Calculate averages per model
        selectedModels.forEach(model => {
          const modelValues = [];
          selectedTestIds.forEach(testId => {
            const test = promptTests.find(t => t.test_id === testId);
            if (test) {
              const modelTestKey = `${model}:${test.name}`;
              if (point[modelTestKey] !== null && point[modelTestKey] !== undefined) {
                modelValues.push(point[modelTestKey]);
              }
            }
          });
          
          if (modelValues.length > 0) {
            const avg = modelValues.reduce((sum, val) => sum + val, 0) / modelValues.length;
            point[`${model}:average`] = avg;
          } else {
            point[`${model}:average`] = null;
          }
        });
        
        return point;
      });
      
      setChartData(formattedData);
    } catch (err) {
      console.error('Error preparing chart data:', err);
      setError('Failed to process data for chart visualization');
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle model selection
  const handleToggleModel = (model) => {
    if (selectedModels.includes(model)) {
      setSelectedModels(selectedModels.filter(m => m !== model));
    } else {
      setSelectedModels([...selectedModels, model]);
    }
  };

  // Toggle all models
  const handleToggleAllModels = () => {
    if (selectedModels.length === availableModels.length) {
      setSelectedModels([]);
    } else {
      setSelectedModels([...availableModels]);
    }
  };

  // Toggle test selection
  const handleToggleTest = (testId) => {
    if (selectedTestIds.includes(testId)) {
      setSelectedTestIds(selectedTestIds.filter(id => id !== testId));
    } else {
      setSelectedTestIds([...selectedTestIds, testId]);
    }
  };

  // Toggle all tests
  const handleToggleAllTests = () => {
    if (selectedTestIds.length === promptTests.length) {
      setSelectedTestIds([]);
    } else {
      setSelectedTestIds(promptTests.map(test => test.test_id));
    }
  };

  // Generate tooltip content
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) {
      return null;
    }

    // Group data by test and model
    const testData = {};
    
    payload.forEach(entry => {
      if (entry.dataKey.includes(':')) {
        const [model, testName] = entry.dataKey.split(':');
        
        // Skip averages in the detailed section
        if (testName === 'average') return;
        
        if (!testData[testName]) {
          testData[testName] = [];
        }
        
        testData[testName].push({
          model,
          value: entry.value,
          color: entry.color
        });
      }
    });
    
    // Sort tests alphabetically
    const sortedTests = Object.keys(testData).sort();
    
    // Sort models (maintain consistent order)
    Object.values(testData).forEach(modelArray => {
      modelArray.sort((a, b) => selectedModels.indexOf(a.model) - selectedModels.indexOf(b.model));
    });
    
    // Extract average data
    const averageData = payload
      .filter(entry => entry.dataKey.includes(':average'))
      .map(entry => ({
        model: entry.dataKey.split(':')[0],
        value: entry.value,
        color: entry.color
      }))
      .sort((a, b) => selectedModels.indexOf(a.model) - selectedModels.indexOf(b.model));

    return (
      <div className="custom-tooltip bg-white p-3 border rounded shadow">
        <p className="font-bold mb-2">Version: {label}</p>
        
        {averageData.length > 0 && (
          <div className="mb-3 border-b pb-2">
            <p className="font-semibold">Average Scores:</p>
            {averageData.map((item, i) => (
              <div key={`avg-${i}`} className="flex justify-between items-center">
                <span style={{ color: item.color }}>{item.model}:</span>
                <span className="font-medium ml-4">
                  {item.value !== null ? `${item.value.toFixed(2)}${showDifference && i > 0 ? ' Δ' : '%'}` : 'No data'}
                </span>
              </div>
            ))}
          </div>
        )}
        
        {sortedTests.map(testName => (
          <div key={testName} className="mb-2">
            <p className="font-semibold">{testName}:</p>
            {testData[testName].map((item, i) => (
              <div key={`${testName}-${i}`} className="flex justify-between items-center">
                <span style={{ color: colorMap[promptTests.find(t => t.name === testName)?.test_id] }}>
                  {item.model}:
                </span>
                <span className="font-medium ml-4">
                  {item.value !== null ? `${item.value.toFixed(2)}${showDifference && i > 0 ? ' Δ' : '%'}` : 'No data'}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };
  
  // Calculate performance summary statistics
  const calculatePerformanceSummary = () => {
    if (chartData.length === 0 || selectedModels.length === 0) {
      return [];
    }
    
    const summary = selectedModels.map(model => {
      const modelData = {};
      let totalScore = 0;
      let totalCount = 0;
      
      // Calculate average score across all versions and tests
      chartData.forEach(point => {
        selectedTestIds.forEach(testId => {
          const test = promptTests.find(t => t.test_id === testId);
          if (test) {
            const modelTestKey = `${model}:${test.name}`;
            if (point[modelTestKey] !== null && point[modelTestKey] !== undefined) {
              totalScore += point[modelTestKey];
              totalCount++;
            }
          }
        });
      });
      
      const avgScore = totalCount > 0 ? totalScore / totalCount : null;
      
      // Calculate improvement from first to last version
      let improvement = null;
      if (chartData.length >= 2) {
        const firstVersion = chartData[0];
        const lastVersion = chartData[chartData.length - 1];
        
        const firstScores = [];
        const lastScores = [];
        
        selectedTestIds.forEach(testId => {
          const test = promptTests.find(t => t.test_id === testId);
          if (test) {
            const modelTestKey = `${model}:${test.name}`;
            
            if (firstVersion[modelTestKey] !== null && firstVersion[modelTestKey] !== undefined) {
              firstScores.push(firstVersion[modelTestKey]);
            }
            
            if (lastVersion[modelTestKey] !== null && lastVersion[modelTestKey] !== undefined) {
              lastScores.push(lastVersion[modelTestKey]);
            }
          }
        });
        
        const firstAvg = firstScores.length > 0 ? firstScores.reduce((sum, val) => sum + val, 0) / firstScores.length : null;
        const lastAvg = lastScores.length > 0 ? lastScores.reduce((sum, val) => sum + val, 0) / lastScores.length : null;
        
        if (firstAvg !== null && lastAvg !== null) {
          improvement = lastAvg - firstAvg;
        }
      }
      
      return {
        model,
        avgScore,
        improvement
      };
    });
    
    // Sort by average score (descending)
    return summary.sort((a, b) => (b.avgScore || 0) - (a.avgScore || 0));
  };
  
  const performanceSummary = calculatePerformanceSummary();

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-col items-start">
        <div className="flex justify-between items-center w-full">
          <h3 className="text-xl font-bold">Model Performance Comparison</h3>
          <div className="flex gap-2">
            <Button 
              size="sm" 
              color={normalizeScores ? "primary" : "default"}
              variant={normalizeScores ? "solid" : "bordered"}
              onClick={() => setNormalizeScores(!normalizeScores)}
              className="text-xs"
            >
              Normalize Scores
            </Button>
            {selectedModels.length > 1 && (
              <Button 
                size="sm" 
                color={showDifference ? "primary" : "default"}
                variant={showDifference ? "solid" : "bordered"}
                onClick={() => setShowDifference(!showDifference)}
                className="text-xs"
              >
                {showDifference ? "Absolute Values" : "Show Differences"}
              </Button>
            )}
          </div>
        </div>

        {/* Model Selection */}
        <div className="w-full mt-3 border rounded-lg p-3">
          <div 
            className="flex justify-between items-center cursor-pointer" 
            onClick={() => setShowModelSelect(!showModelSelect)}
          >
            <h4 className="text-md font-semibold">Models</h4>
            <div className="flex items-center">
              <span className="text-sm text-gray-500 mr-2">
                {selectedModels.length} of {availableModels.length} selected
              </span>
              {showModelSelect ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
          </div>
          
          {showModelSelect && (
            <div className="mt-2">
              <div className="flex items-center mb-2">
                <Button 
                  size="sm" 
                  variant="flat" 
                  onClick={handleToggleAllModels}
                  className="text-xs"
                >
                  {selectedModels.length === availableModels.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {availableModels.map((model, index) => (
                  <Button
                    key={model}
                    size="sm"
                    variant={selectedModels.includes(model) ? "solid" : "bordered"}
                    onClick={() => handleToggleModel(model)}
                    className="text-xs"
                    style={{
                      borderStyle: lineStyles[index % lineStyles.length].strokeDasharray === "0" ? "solid" : "dashed",
                      borderColor: "#000",
                      color: selectedModels.includes(model) ? '#fff' : '#000',
                      backgroundColor: selectedModels.includes(model) ? '#000' : 'transparent'
                    }}
                  >
                    {model}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Test Selection */}
        <div className="w-full mt-3 border rounded-lg p-3">
          <div 
            className="flex justify-between items-center cursor-pointer" 
            onClick={() => setShowTestSelect(!showTestSelect)}
          >
            <h4 className="text-md font-semibold">Tests</h4>
            <div className="flex items-center">
              <span className="text-sm text-gray-500 mr-2">
                {selectedTestIds.length} of {promptTests.length} selected
              </span>
              {showTestSelect ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
          </div>
          
          {showTestSelect && (
            <div className="mt-2">
              <div className="flex items-center mb-2">
                <Button 
                  size="sm" 
                  variant="flat" 
                  onClick={handleToggleAllTests}
                  className="text-xs"
                >
                  {selectedTestIds.length === promptTests.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {promptTests.map(test => (
                  <Button
                    key={test.test_id}
                    size="sm"
                    variant={selectedTestIds.includes(test.test_id) ? "solid" : "bordered"}
                    onClick={() => handleToggleTest(test.test_id)}
                    className="text-xs"
                    style={{
                      borderColor: colorMap[test.test_id],
                      color: selectedTestIds.includes(test.test_id) ? '#fff' : colorMap[test.test_id],
                      backgroundColor: selectedTestIds.includes(test.test_id) ? colorMap[test.test_id] : 'transparent'
                    }}
                  >
                    {test.name}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardHeader>

      <CardBody className="pt-2">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : error ? (
          <div className="flex justify-center items-center h-64 text-red-500">{error}</div>
        ) : chartData.length === 0 || selectedTestIds.length === 0 || selectedModels.length === 0 ? (
          <div className="flex justify-center items-center h-64 text-gray-400">
            Select at least one model and one test to view data
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={500}>
            <LineChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="version" 
                label={{ 
                  value: 'Version', 
                  position: 'insideBottom', 
                  offset: -5 
                }}
              />
              <YAxis 
                domain={showDifference ? null : [0, 100]}
                label={{ 
                  value: showDifference ? 'Difference (%)' : 'Average Score (%)', 
                  angle: -90, 
                  position: 'insideLeft' 
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              
              {/* Determine if we should show individual test lines or just averages */}
              {(() => {
                const showDetailedView = (selectedModels.length * selectedTestIds.length < 30) || (selectedModels.length > 2 && selectedTestIds.length <= 5) ;
                
                if (showDetailedView) {
                  // Show both individual tests and averages
                  return (
                    <>
                      {/* Test-specific lines */}
                      {selectedModels.map((model, modelIndex) => (
                        selectedTestIds.map(testId => {
                          const test = promptTests.find(t => t.test_id === testId);
                          if (!test) return null;
                          
                          const modelTestKey = `${model}:${test.name}`;
                          const testColor = colorMap[test.test_id];
                          const style = lineStyles[modelIndex % lineStyles.length];
                          
                          return (
                            <Line
                              key={modelTestKey}
                              type="monotone"
                              dataKey={modelTestKey}
                              name={`${test.name}`}
                              stroke={testColor}
                              strokeDasharray={style.strokeDasharray}
                              dot={{ r: 4 }}
                              activeDot={{ r: 6 }}
                              connectNulls
                            />
                          );
                        })
                      ))}
                      
                      {/* Average lines for each model (thicker) */}
                      {selectedModels.map((model, modelIndex) => {
                        const dataKey = `${model}:average`;
                        const style = lineStyles[modelIndex % lineStyles.length];
                        
                        return (
                          <Line
                            key={dataKey}
                            type="monotone"
                            dataKey={dataKey}
                            name={`${model} - Average`}
                            stroke="#000"
                            strokeDasharray={style.strokeDasharray}
                            strokeWidth={3}
                            dot={{ r: 4, strokeWidth: 2 }}
                            activeDot={{ r: 6 }}
                            connectNulls
                          />
                        );
                      })}
                    </>
                  );
                } else {
                  // Only show model averages for cleaner visualization when many tests are selected
                  return (
                    <>
                      {/* Average lines for each model (thicker) */}
                      {selectedModels.map((model, modelIndex) => {
                        const dataKey = `${model}:average`;
                        // take color by modelIndex as int
                        const color = colorMap[promptTests[modelIndex % promptTests.length].test_id];
                        
                        return (
                          <Line
                            key={dataKey}
                            type="monotone"
                            dataKey={dataKey}
                            name={`${model}`}
                            stroke={color}
                            strokeWidth={10}
                            dot={{ r: 5, strokeWidth: 2 }}
                            activeDot={{ r: 7 }}
                            connectNulls
                          />
                        );
                      })}
                    </>
                  );
                }
              })()}
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardBody>

      {performanceSummary.length > 0 && (
        <CardFooter className="flex flex-col items-start">
          <h4 className="text-md font-semibold mb-2">Performance Summary</h4>
          
          <div className="w-full overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="py-2 px-3 text-left">Model</th>
                  <th className="py-2 px-3 text-right">Avg Score</th>
                  <th className="py-2 px-3 text-right">Improvement</th>
                  <th className="py-2 px-3 text-center">Ranking</th>
                </tr>
              </thead>
              <tbody>
                {performanceSummary.map((summary, index) => (
                  <tr key={summary.model} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                    <td className="py-2 px-3 font-medium">{summary.model}</td>
                    <td className="py-2 px-3 text-right">
                      {summary.avgScore !== null ? `${summary.avgScore.toFixed(2)}%` : 'N/A'}
                    </td>
                    <td className="py-2 px-3 text-right">
                      {summary.improvement !== null ? (

<span className={summary.improvement >= 0 ? 'text-green-500' : 'text-red-500'}>
                          {summary.improvement > 0 ? '+' : ''}{summary.improvement.toFixed(2)}%
                        </span>
                      ) : 'N/A'}
                    </td>
                    <td className="py-2 px-3 text-center">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-200">
                        {index + 1}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="mt-3 text-sm text-gray-500">
            <p>Chart shows performance comparison across models and versions for selected tests</p>
            {showDifference && selectedModels.length > 1 && (
              <p className="mt-1">Differences are calculated relative to {selectedModels[0]} as the baseline</p>
            )}
            {normalizeScores && (
              <p className="mt-1">Scores are normalized based on test thresholds</p>
            )}
          </div>
        </CardFooter>
      )}
    </Card>
  );
};

export default ModelComparisonChart;